import { Tab, TabsGroup } from '@billy/design-system'
import React, { MouseEvent, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router'

import { settingsRouteToEmberSettingsRoute } from '../../constants/settingsRouteToEmberSettingsRoute'
import { userSettingsRoutes } from '../../constants/userSettingsRoutes'
import { SettingsRoute } from '../../enums/settingsRoute'
import { useHiddenTabsCount } from '../../hooks/useHiddenTabsCount'
import { routeToInternalSettings } from '../../utils/routeToInternalSettings'
import { useOrganizationSettingsTabItems } from './hooks/useOrganizationSettingsTabItems'
import { useUserSettingsTabItems } from './hooks/useUserSettingsTabItems'

export const SettingsNavigation = () => {
  const location = useLocation()
  const { items: userRoutes } = useUserSettingsTabItems()
  const { items: organizationRoutes } = useOrganizationSettingsTabItems({ withCardPaymentsBadge: true })

  const currentRouteName = location.pathname
  const isUserRoute = useMemo(() => userSettingsRoutes.includes(currentRouteName as SettingsRoute), [currentRouteName])
  const routes = useMemo(
    () => (isUserRoute ? userRoutes : organizationRoutes),
    [isUserRoute, userRoutes, organizationRoutes],
  )
  const count = useHiddenTabsCount(routes.length)

  const handleSettingsTabClick = useCallback(
    (route: SettingsRoute) => (event?: MouseEvent<HTMLElement>) => {
      /* Both `to` (technically <a> link wrapper) and `onClick` configured on the Tab. Link is more correct from accessibility standpoint but we need this handler func for navigation to work properly because of the routing setup.
      This is to keep both. `event` is missing when the tab is selected via TabsGroup's overflow "More" dropdown instead of clicked directly. */
      event?.preventDefault()
      routeToInternalSettings(settingsRouteToEmberSettingsRoute[route])
    },
    [],
  )

  return (
    <TabsGroup hiddenTabsCount={count}>
      {routes.map(({ badgeCounter, badgeLabel, route, childRoutes, text, hidden }) => {
        const doesCurrentRouteContainChildRoute = childRoutes?.some((childRoute) =>
          currentRouteName.includes(childRoute),
        )

        return (
          !hidden && (
            <Tab
              active={currentRouteName === route || (childRoutes && doesCurrentRouteContainChildRoute)}
              badgeCounter={badgeCounter}
              badgeLabel={badgeLabel}
              key={route}
              onClick={handleSettingsTabClick(route)}
              to={route}
            >
              {text}
            </Tab>
          )
        )
      })}
    </TabsGroup>
  )
}
